import { Button, DatePicker, Select, Space, Table, TableProps, Tag, Tooltip, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getSalaryPayoutBatchMonth, listMonthlySalaryPayouts } from '~/services/salary-payout'
import {
  ISalaryPayoutBatchMonthRes,
  ITeacherMonthlySalarySummary,
  TeacherSalaryPayoutStatus,
} from '~/types/salary-payout'

import BatchPanel, { activeBatchOf } from './BatchPanel'
import PayoutDetailDrawer from './PayoutDetailDrawer'
import { Container } from './style'

const STATUS_COLOR: Record<TeacherSalaryPayoutStatus, string> = {
  QUOTE_CREATED: 'blue',
  TRANSFER_CREATED: 'geekblue',
  FUNDED: 'gold',
  PROCESSING: 'orange',
  OUTGOING_PAYMENT_SENT: 'green',
  BOUNCED_BACK: 'red',
  CANCELLED: 'volcano',
  REFUNDED: 'magenta',
  FAILED: 'red',
}

const STATUS_LABEL: Record<TeacherSalaryPayoutStatus, string> = {
  QUOTE_CREATED: '已建报价',
  TRANSFER_CREATED: '已建Transfer',
  FUNDED: '已出资',
  PROCESSING: 'Wise处理中',
  OUTGOING_PAYMENT_SENT: '钱已寄出',
  BOUNCED_BACK: '银行退回',
  CANCELLED: '已取消',
  REFUNDED: '已退款',
  FAILED: '失败',
}

type StatusFilter = 'ALL' | TeacherSalaryPayoutStatus | 'NOT_STARTED' | 'METHOD_UNVERIFIED'

/** 还没有转账、或上一笔转账没把钱发到（取消 / 银行退回 / 退款），可以（重新）报价的发放状态。和后端一致。 */
const RE_QUOTABLE: (TeacherSalaryPayoutStatus | undefined)[] = [
  undefined, 'QUOTE_CREATED', 'FAILED', 'CANCELLED', 'BOUNCED_BACK', 'REFUNDED',
]

/** 能勾选加入批次的老师：收款方式已审核、本月有钱要发、还没建转账。月份没结算完的由后端拒绝并说明原因。 */
const batchBlockReason = (row: ITeacherMonthlySalarySummary): string | null => {
  if (!row.payoutMethodVerified) return '收款方式未审核'
  if (!(Number(row.netSalary) > 0)) return '本月实发为 0'
  if (!RE_QUOTABLE.includes(row.payout?.status)) {
    return row.payout?.batchId ? '已在批次中' : '已有单笔转账'
  }
  return null
}

const SalaryPayoutPage = () => {
  const [yearMonth, setYearMonth] = useState<Dayjs>(dayjs().subtract(1, 'month'))
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<ITeacherMonthlySalarySummary[]>([])
  const [page, setPage] = useState(1)
  // 一页放下所有老师，批次勾选才不会漏掉别的页
  const [size, setSize] = useState(50)
  const [total, setTotal] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<ITeacherMonthlySalarySummary | null>(null)
  const [batchData, setBatchData] = useState<ISalaryPayoutBatchMonthRes | null>(null)
  const [batchLoading, setBatchLoading] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])

  const year = yearMonth.year()
  const month = yearMonth.month() + 1

  // 只认最后一次请求的结果：切换月份时，上个月迟到的响应不能覆盖这个月的表格（勾选就是按表格来的）
  const latestRequest = useRef(0)
  const [loadedMonth, setLoadedMonth] = useState<string | null>(null)

  const fetchData = useCallback(() => {
    const request = ++latestRequest.current
    const ym = `${year}-${month}`
    setLoading(true)
    listMonthlySalaryPayouts(year, month, page, size)
      .then((res) => {
        if (request !== latestRequest.current) return
        setDataSource(res.data?.content || [])
        setTotal(res.data?.total || 0)
        setLoadedMonth(ym)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message
        message.error(msg || '加载失败')
      })
      .finally(() => setLoading(false))
  }, [year, month, page, size])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const fetchBatch = useCallback(() => {
    setBatchLoading(true)
    getSalaryPayoutBatchMonth(year, month)
      .then((res) => setBatchData(res.data))
      .catch((err) => {
        setBatchData(null)
        message.error(err?.response?.data?.message || err?.message || '批次加载失败')
      })
      .finally(() => setBatchLoading(false))
  }, [year, month])

  useEffect(() => {
    fetchBatch()
  }, [fetchBatch])

  const refreshAll = useCallback(() => {
    fetchData()
    fetchBatch()
  }, [fetchData, fetchBatch])

  const activeBatch = activeBatchOf(batchData)
  const batchClosed = activeBatch?.status === 'COMPLETED'

  // 每个月份第一次加载完时默认勾上所有能发的老师。之后的刷新不再重置，管理员取消勾选的人不会被悄悄勾回。
  const defaultedMonth = useRef<string | null>(null)
  useEffect(() => {
    if (!loadedMonth || defaultedMonth.current === loadedMonth) return
    defaultedMonth.current = loadedMonth
    setSelectedKeys(dataSource.filter((r) => !batchBlockReason(r)).map((r) => String(r.teacherId)))
  }, [loadedMonth, dataSource])

  const filtered = useMemo(() => {
    if (statusFilter === 'ALL') return dataSource
    if (statusFilter === 'NOT_STARTED') {
      return dataSource.filter((r) => r.payoutMethodVerified && !r.payout)
    }
    if (statusFilter === 'METHOD_UNVERIFIED') {
      return dataSource.filter((r) => !r.payoutMethodVerified)
    }
    return dataSource.filter((r) => r.payout?.status === statusFilter)
  }, [dataSource, statusFilter])

  const onRowClick = useCallback((row: ITeacherMonthlySalarySummary) => {
    setActiveRow(row)
    setDrawerOpen(true)
  }, [])

  const columns = useMemo<TableProps<ITeacherMonthlySalarySummary>['columns']>(() => [
    {
      title: '老师',
      dataIndex: 'teacherName',
      key: 'teacherName',
      width: 160,
      fixed: 'left',
      render: (name: string, row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <div style={{ color: '#9ca3af', fontSize: 12 }}>
            #{row.teacherId} {row.email}
          </div>
        </div>
      ),
    },
    {
      title: '收款方式',
      dataIndex: 'payoutMethodVerified',
      key: 'payoutMethodVerified',
      width: 110,
      align: 'center',
      render: (v: boolean) =>
        v ? <Tag color="green">已审核</Tag> : <Tag color="orange">未审核</Tag>,
    },
    {
      title: '币种',
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
      align: 'center',
    },
    {
      title: '每节',
      dataIndex: 'ratePerClass',
      key: 'ratePerClass',
      width: 90,
      align: 'right',
    },
    {
      title: '完成 / 学缺',
      key: 'classCounts',
      width: 110,
      align: 'center',
      render: (_, row) => `${row.completedClassCount} / ${row.studentAbsentCount}`,
    },
    {
      title: '老师缺勤',
      dataIndex: 'teacherAbsentCount',
      key: 'teacherAbsentCount',
      width: 90,
      align: 'center',
    },
    {
      title: '净薪资',
      dataIndex: 'netSalary',
      key: 'netSalary',
      width: 130,
      align: 'right',
      render: (v: number, row) => (
        <span style={{ fontWeight: 600 }}>
          {v} {row.currency}
        </span>
      ),
    },
    {
      title: '支付状态',
      key: 'status',
      width: 140,
      align: 'center',
      render: (_, row) => {
        const s = row.payout?.status
        if (!s) {
          return row.payoutMethodVerified ? (
            <Tag>未发放</Tag>
          ) : (
            <Tag color="default">收款方式未审核</Tag>
          )
        }
        return <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
      },
    },
    {
      title: 'Wise 实际',
      key: 'wise',
      width: 180,
      render: (_, row) => {
        if (!row.payout) return '—'
        const fee = row.payout.wiseFee
        const rate = row.payout.wiseRate
        return (
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            <div>
              {row.payout.wiseSourceAmount ?? '—'} {row.payout.sourceCurrency}
              {' → '}
              {row.payout.wiseTargetAmount ?? '—'} {row.payout.targetCurrency}
            </div>
            <div style={{ color: '#9ca3af' }}>
              fee {fee ?? '—'} · rate {rate ?? '—'}
            </div>
          </div>
        )
      },
    },
    {
      title: '批次',
      key: 'batch',
      width: 110,
      align: 'center',
      render: (_, row) => {
        const p = row.payout
        if (!p || RE_QUOTABLE.includes(p.status)) return <span style={{ color: '#9ca3af' }}>—</span>
        if (p.batchId && String(p.batchId) === String(activeBatch?.id)) return <Tag color="blue">本月批次</Tag>
        if (p.batchId) return <Tag>已取消的批次</Tag>
        return <Tag color="purple">单笔</Tag>
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      align: 'center',
      render: (_, row) => (
        <Button type="link" onClick={() => onRowClick(row)}>
          查看 / 操作
        </Button>
      ),
    },
  ], [onRowClick, activeBatch?.id])

  // 只提交当前看得见、而且仍可加入的老师：被状态筛选藏起来的、刷新后已失效的勾选都不算
  const submittableIds = useMemo(
    () => new Set(filtered.filter((r) => !batchBlockReason(r)).map((r) => String(r.teacherId))),
    [filtered],
  )
  const selectedTeacherIds = useMemo(
    () => selectedKeys.filter((k) => submittableIds.has(k)),
    [selectedKeys, submittableIds],
  )

  return (
    <Container>
      <div className="top">
        <Space size={12} wrap>
          <span>结算月份：</span>
          <DatePicker
            picker="month"
            value={yearMonth}
            allowClear={false}
            onChange={(d) => {
              if (d) {
                setYearMonth(d)
                setPage(1)
              }
            }}
          />
          <span>状态筛选：</span>
          <Select
            value={statusFilter}
            style={{ width: 180 }}
            onChange={(v) => setStatusFilter(v)}
            options={[
              { value: 'ALL', label: '全部' },
              { value: 'METHOD_UNVERIFIED', label: '收款方式未审核' },
              { value: 'NOT_STARTED', label: '本月未发放' },
              { value: 'QUOTE_CREATED', label: STATUS_LABEL.QUOTE_CREATED },
              { value: 'TRANSFER_CREATED', label: STATUS_LABEL.TRANSFER_CREATED },
              { value: 'FUNDED', label: STATUS_LABEL.FUNDED },
              { value: 'PROCESSING', label: STATUS_LABEL.PROCESSING },
              { value: 'OUTGOING_PAYMENT_SENT', label: STATUS_LABEL.OUTGOING_PAYMENT_SENT },
              { value: 'BOUNCED_BACK', label: STATUS_LABEL.BOUNCED_BACK },
              { value: 'CANCELLED', label: STATUS_LABEL.CANCELLED },
              { value: 'REFUNDED', label: STATUS_LABEL.REFUNDED },
              { value: 'FAILED', label: STATUS_LABEL.FAILED },
            ]}
          />
          <Button type="primary" onClick={refreshAll} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      <BatchPanel
        year={year}
        month={month}
        data={batchData}
        loading={batchLoading}
        selectedTeacherIds={selectedTeacherIds}
        onChanged={refreshAll}
      />

      <div className="table">
        <Table
          rowKey={(r) => String(r.teacherId)}
          loading={loading}
          columns={columns}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setSelectedKeys(keys.map(String)),
            getCheckboxProps: (row) => ({ disabled: batchClosed || !!batchBlockReason(row) }),
            renderCell: (_checked, row, _index, node) => {
              const reason = batchClosed ? '本月批次已锁定' : batchBlockReason(row)
              return reason ? <Tooltip title={reason}>{node}</Tooltip> : node
            },
          }}
          dataSource={filtered}
          scroll={{ x: 1400, y: 560 }}
          pagination={{
            total,
            pageSize: size,
            current: page,
            onChange: (p, s) => {
              setPage(p)
              setSize(s)
            },
            showTotal: (t) => `共 ${t} 位老师`,
          }}
        />
      </div>

      <PayoutDetailDrawer
        open={drawerOpen}
        summary={activeRow}
        year={year}
        month={month}
        onClose={() => setDrawerOpen(false)}
        onRefresh={fetchData}
        onJumpToMonth={(y, m) => {
          setYearMonth(dayjs(`${y}-${String(m).padStart(2, '0')}-01`))
          setPage(1)
        }}
      />
    </Container>
  )
}

export default memo(SalaryPayoutPage)
